import { FC } from 'react';
import { Tile, Link, Tag } from '@carbon/react';
import { CheckmarkFilled, InProgress } from '@carbon/react/icons';
import { ConfidenceBreakdown } from 'components/confidence-breakdown';
import { AddressReviewFlag } from './address-review-flag';
import { EntityClassification, AliasesList } from './reconcile/alias-fields';
import type { AliasClusterInfo } from './reconcile/use-alias-pill-states';
import type { SuggestionSummary } from './types';
import { ENTITY_TYPE_LABEL } from './reconcile/types';
import { FIRST_SUGGESTION_LABEL } from './utils';
import { formatConfidence } from 'utils/number';
import style from './review-match.module.scss';

interface Props {
  suggestion: SuggestionSummary;
  // The eyebrow: "Suggestion N" / "User Selected" / "User Created" (see
  // suggestionLabels), or the resolved tile's own label.
  label: string;
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  // A resolved match tile: its confidence shows only when a scored suggestion
  // stands behind it (an Entities-screen merge has none).
  resolved?: boolean;
  // Alias-cluster info for this entity (role + peer names), computed by the
  // parent across the visible match set. Drives the Entity Classification +
  // Aliases fields.
  aliasInfo?: AliasClusterInfo;
  // When set, a confirmed (non-provisional) match renders its badge as "Correct"
  // rather than "Confirmed" — used for reconcile outcomes (dedupe/alias), which
  // complete the match implicitly.
  correctBadge?: boolean;
}

export const ReviewMatch: FC<Props> = ({
  suggestion,
  label,
  selected,
  disabled,
  onSelect,
  resolved,
  aliasInfo,
  correctBadge,
}) => {
  const entity = suggestion.displayEntity ?? suggestion.suggestedEntity;
  if (!entity) return null;

  const isConfirmed = suggestion.status === 'confirmed';
  const isUserCreated = suggestion.userCreated === true;
  const isUserSelected = suggestion.userSelected && !isUserCreated;
  const isImported = entity.type === 'ImportedEntity';
  const isProvisional = isConfirmed && isImported;
  const showRecommendedBadge =
    label === FIRST_SUGGESTION_LABEL && suggestion.isAiRecommendation;
  // User rows never have a match score. A resolved tile keeps the confidence of
  // the scored suggestion a reconcile preserves.
  const hideConfidence =
    isUserSelected ||
    isUserCreated ||
    (!!resolved && suggestion.confidenceScore === null);

  const entityTypeLabel = ENTITY_TYPE_LABEL[entity.type];

  const tileClass = [
    style.card,
    isImported ? style.cardImported : '',
    isProvisional
      ? style.cardInProgress
      : isConfirmed
        ? style.cardConfirmed
        : '',
    selected && !isConfirmed ? style.cardSelected : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = () => {
    if (disabled || isConfirmed) return;
    onSelect?.();
  };

  return (
    <Tile
      className={tileClass}
      onClick={handleClick}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
    >
      <div className={style.header}>
        <span className={style.eyebrow}>{label}</span>
        {isProvisional ? (
          <span className={style.inProgressBadge}>
            <InProgress size={16} /> In Progress
          </span>
        ) : (
          isConfirmed && (
            <span className={style.confirmedBadge}>
              <CheckmarkFilled size={16} />{' '}
              {isUserCreated || correctBadge ? 'Correct' : 'Confirmed'}
            </span>
          )
        )}
        {showRecommendedBadge && (
          <Tag type="cyan" size="sm" className={style.recommendedBadge}>
            Recommended
          </Tag>
        )}
      </div>

      <h5 className={style.name}>
        <Link
          href={`/entities/${entity.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {entity.name || '—'}
        </Link>
      </h5>

      <dl className={style.fields}>
        <div className={style.field}>
          <dt>Entity Type</dt>
          <dd className={isImported ? style.entityTypeImported : undefined}>
            {entityTypeLabel}
          </dd>
        </div>
        <div className={style.field}>
          <dt>Entity ID</dt>
          <dd>{entity.id}</dd>
        </div>
        <div className={style.field}>
          <dt>Entity Classification</dt>
          <dd>
            <EntityClassification info={aliasInfo} entityId={entity.id} />
          </dd>
        </div>
        <div className={style.field}>
          <dt>Aliases</dt>
          <dd>
            <AliasesList
              aliases={aliasInfo?.aliases ?? []}
              entityId={entity.id}
            />
          </dd>
        </div>
        <div className={style.field}>
          <dt>Address</dt>
          <dd>
            {entity.primaryAddress?.fullText || '—'}
            {entity.primaryAddress?.reviewFlag && (
              <>
                {' '}
                <AddressReviewFlag flag={entity.primaryAddress.reviewFlag} />
              </>
            )}
          </dd>
        </div>
        {entity.secondaryAddress && (
          <div className={style.field}>
            <dt>Secondary Address</dt>
            <dd>
              {entity.secondaryAddress.fullText}
              {entity.secondaryAddress.reviewFlag && (
                <>
                  {' '}
                  <AddressReviewFlag
                    flag={entity.secondaryAddress.reviewFlag}
                  />
                </>
              )}
            </dd>
          </div>
        )}
        {entity.totalAddressesCount > 2 && (
          <div className={style.field}>
            <dd className={style.moreAddressesNote}>
              To see all {entity.totalAddressesCount} addresses, please visit
              the{' '}
              <Link href={`/entities/${entity.id}`}>entity detail page</Link>.
            </dd>
          </div>
        )}
        <div className={style.field}>
          <dt>Phone</dt>
          <dd>{entity.phoneNumber || '—'}</dd>
        </div>
        {entity.emailAddress && (
          <div className={style.field}>
            <dt>Email</dt>
            <dd>{entity.emailAddress}</dd>
          </div>
        )}
      </dl>

      {!hideConfidence && (
        <>
          <div className={style.confidenceRow}>
            <span className={style.confidenceLabel}>Confidence</span>
            <span className={style.confidenceValue}>
              {formatConfidence(suggestion.confidenceScore)}
            </span>
          </div>
          {suggestion.confidenceFactors &&
            suggestion.confidenceFactors.length > 0 && (
              <div className={style.breakdown}>
                <span className={style.breakdownLabel}>
                  Confidence Breakdown
                </span>
                <ConfidenceBreakdown factors={suggestion.confidenceFactors} />
              </div>
            )}
        </>
      )}
    </Tile>
  );
};
